import { useEffect, useState, useMemo, Fragment } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Typography,
    styled,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Autocomplete,
    Stack
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { IconX, IconAdjustmentsHorizontal } from '@tabler/icons';
import { Check, ExpandMore, Star } from '@mui/icons-material';
import { debounce, split } from 'lodash';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

import { numberProps, boxProps, selectProps, datetimeProps } from 'views/common/GlobalFilter';
import { ButtonStyled } from 'views/common/ControlsStyle';
import { RandomSetting } from 'types/container';
import { TagData } from 'types/tag';
import RequiredIndicator from 'views/common/RequiredIndicator';

export interface TypeCheckbox extends TagData {
    checked?: boolean;
}

export interface AddObjectProps {
    onClose: () => void;
    setFilters?: (val: RandomSetting[]) => void;
    availabeFilters?: any;
    randomSettings?: RandomSetting[];
}

const TitleText = styled(Typography)({
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px',
    textAlign: 'left',
    margin: 0,
    marginTop: '20px',
    marginBottom: '6.5px'
});

const AddObject = (props: AddObjectProps) => {
    const { availabeFilters, randomSettings, onClose, setFilters } = props;
    const [, setSearchText] = useState<string>();
    const [boxSection, setBoxSection] = useState<boxProps>();
    const [numberSection, setNumberSection] = useState<numberProps>();
    const [selectSection, setSelectSection] = useState<selectProps>();
    const [datetimeSection, setDatetimeSection] = useState<datetimeProps>();
    const [newFilters, setNewFilters] = useState<RandomSetting[]>([]);
    const [disabledSave, setDisabledSave] = useState(true);

    useEffect(() => {
        availabeFilters.forEach((element: any) => {
            const data: any = element.items;
            switch (element.title) {
                case 'number': {
                    setNumberSection(data);
                    break;
                }
                case 'select': {
                    setSelectSection(data);
                    break;
                }
                case 'box': {
                    data.options.filter((o: any) => o.name !== 'unitType');
                    setBoxSection(data);
                    break;
                }
                case 'datetime': {
                    setDatetimeSection(data);
                    break;
                }
            }
        });
    }, [availabeFilters]);

    useEffect(() => {
        if (randomSettings) {
            setNewFilters(randomSettings);
        }
    }, [randomSettings]);

    useEffect(() => {
        const namesToCheck = ['amountQuestions', 'mainTag', 'subTag'];
        const bothExist = namesToCheck.every((nameToCheck) => {
            if (nameToCheck === 'amountQuestions') {
                return newFilters.some((item) => item.name === nameToCheck);
            }
            if (nameToCheck === 'mainTag' || nameToCheck === 'subTag') {
                return newFilters.some((item) => item.name === 'mainTag' || item.name === 'subTag');
            }
            return false;
        });
        if (bothExist) setDisabledSave(false);
        else setDisabledSave(true);
    }, [newFilters]);

    const debouncedSearchTextChange = useMemo(() => debounce(setSearchText, 300), []);

    // updates the results
    const handleToggle = (index: number) => () => {
        setNewFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));
    };

    // control each checkbox in acordions
    function handleOnChange(e: any, item: any) {
        const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
        if (e.target.checked) {
            const filterToUpdate = newFilters.find((obj) => obj.name === item.name);
            // let currentValue = [];
            let ids: string[] = [];
            let names: string[] = [];
            if (filterToUpdate) {
                ids = split(filterToUpdate.value, ',');
                names = split(filterToUpdate.display_name, ',');
            }

            setNewFilters([
                ...removedKeyFilter,
                {
                    name: item.name,
                    value: [...ids, e.target.value].join(),
                    checked: true,
                    display_name: [...names, e.target.name].join()
                }
            ]);
        } else {
            const filterToUpdate = newFilters.find((obj) => obj.name === item.name);
            if (filterToUpdate) {
                const ids = split(filterToUpdate.value, ',');
                const names = split(filterToUpdate.display_name, ',');
                if (ids.length === 1) {
                    setNewFilters(removedKeyFilter);
                    return;
                }
                const newIds = ids.filter((val: any) => val !== e.target.value);
                const newNames = names.filter((val: any) => val !== e.target.name);
                setNewFilters([...removedKeyFilter, { ...filterToUpdate, value: newIds.join(), display_name: newNames.join() }]);
            } else {
                setNewFilters(removedKeyFilter);
            }
        }
    }

    function uniqueArrayByProperty(arr: any, property: string) {
        const uniqueArr = [];
        const uniqueValues = new Set();

        for (const obj of arr) {
            const key = obj[property];
            const isStringKey = typeof key === 'string';

            if (!uniqueValues.has(isStringKey ? key : key.toString())) {
                uniqueArr.push(obj);
                uniqueValues.add(isStringKey ? key : key.toString());
            }
        }

        return uniqueArr;
    }

    // handle multiple tags
    function handleTagChange(type: string, event: React.ChangeEvent<{}>, newValue: { id: string; name: string }[] | [], reason: string) {
        const res = uniqueArrayByProperty(newValue, 'id');
        const ids = res.map((tag) => tag.id);
        const names = res.map((tag) => tag.name);

        const removedKeyFilter = newFilters.filter((obj) => obj.name !== type);
        const newFilterEntry = {
            name: type,
            value: ids.length > 0 ? ids.join(',').replace(/^,/, '') : '',
            checked: true,
            display_name: names.length > 0 ? names.join(',').replace(/^,/, '') : ''
        };

        // Only add the new entry if either value or display_name is non-empty
        if (newFilterEntry.value || newFilterEntry.display_name) {
            setNewFilters([...removedKeyFilter, newFilterEntry]);
        } else {
            setNewFilters(removedKeyFilter);
        }

        if (reason === 'clear') {
            setNewFilters(removedKeyFilter);
        }
    }
    const mapFilterToAutocompleteValue = (filter: any) => {
        const valueArray = filter?.value?.split(',');
        const displayNameArray = filter?.display_name?.split(',');

        if (valueArray && displayNameArray && Array.isArray(valueArray) && Array.isArray(displayNameArray)) {
            return valueArray.map((value: any, index: number) => ({
                id: value,
                name: displayNameArray[index]?.trim() || filter.display_name,
                display_name: displayNameArray[index]?.trim() || filter.display_name
            }));
        }

        return [];
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', height: '100%' }}>
            <Box mt="12px" display="flex" justifyContent="space-between" alignItems="center">
                <TitleText variant="h3">
                    <FormattedMessage id="search-random" />
                </TitleText>
                <IconButton onClick={onClose}>
                    <IconX height="14px" width="14px" />
                </IconButton>
            </Box>
            <Box>
                <FilterList newFilters={newFilters} handleToggle={handleToggle} />
                <TextField
                    fullWidth
                    label={<FormattedMessage id="object-search" />}
                    onChange={(e) => debouncedSearchTextChange(e.target.value)}
                    sx={{ marginTop: 3.75 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <IconAdjustmentsHorizontal style={{ color: '#E4DBF0' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Box mt={2.5}>
                    <Box>
                        <Typography sx={{ marginLeft: 1, mb: 1.25, color: '#878E95' }}>
                            <FormattedMessage id="label-search" />
                        </Typography>

                        {boxSection && boxSection?.options.filter((e) => e.values).length === boxSection?.options.length && (
                            <>
                                <Box mt="28px" mb="24px">
                                    {boxSection?.options
                                        .filter((o: any) => o.name !== 'unitType')
                                        .map((item, index) => (
                                            <Accordion key={index} sx={{ '&:before': { display: 'none' } }}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    aria-controls={item.label}
                                                    id={`panel${index}-header`}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Star />
                                                        <Typography sx={{ flexGrow: 1, marginLeft: 1, color: '#222529' }}>
                                                            <FormattedMessage id={item.label} values={{ indicator: '' }} />
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <List>
                                                        {Object.keys(item.values).map((key: string) => (
                                                            <ListItem key={key}>
                                                                <Checkbox
                                                                    onChange={(e) => handleOnChange(e, item)}
                                                                    value={key}
                                                                    name={item.values[key].name}
                                                                    checked={
                                                                        !!newFilters.find(
                                                                            (f) => f.name === item.name && split(f.value, ',').includes(key)
                                                                        )
                                                                    }
                                                                />
                                                                <ListItemText
                                                                    primary={
                                                                        <FormattedMessage
                                                                            id={item.values[key].name}
                                                                            defaultMessage={item.values[key].name}
                                                                        />
                                                                    }
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                </Box>
                            </>
                        )}
                    </Box>
                    <Box mt={6.25}>
                        {numberSection && (
                            <>
                                {numberSection?.options.map((item, index) => (
                                    <Box key={`text-field-number-${index}`} sx={{ mb: 6.25 }}>
                                        <Typography sx={{ marginLeft: 1, color: '#878E95' }}>
                                            <FormattedMessage id={item.label} values={{ level: '', indicator: <RequiredIndicator /> }} />
                                        </Typography>

                                        <TextField
                                            type="number"
                                            label={<FormattedMessage id="enter-value" />}
                                            inputProps={{ min: item.min, max: item.max, step: item.step }}
                                            sx={{ width: '10rem', marginTop: 1.25 }}
                                            value={newFilters.find((f) => f.name === item.name)?.value || ''}
                                            onChange={(e) => {
                                                const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                                setNewFilters([
                                                    ...removedKeyFilter,
                                                    { name: item.name, value: e.target.value, checked: true }
                                                ]);
                                            }}
                                        />
                                    </Box>
                                ))}
                            </>
                        )}

                        <Typography sx={{ marginLeft: 1, color: '#878E95' }}>
                            <FormattedMessage id="labeling" values={{ indicator: <RequiredIndicator /> }} />
                        </Typography>
                        {selectSection && selectSection?.options.filter((e) => e.values).length === selectSection?.options.length && (
                            <>
                                <Box mt="28px" mb="24px">
                                    {selectSection?.options.map((item: any, index) => (
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            disableCloseOnSelect
                                            sx={{ mt: 2 }}
                                            key={`text-field-select${index}`}
                                            options={Object.keys(item.values).map(
                                                (key: string): TagData => ({
                                                    id: item.values[key],
                                                    name: key
                                                })
                                            )}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderOption={(optionProps, option, { selected }) => (
                                                <MenuItem
                                                    {...optionProps}
                                                    key={option.id}
                                                    value={option.id}
                                                    sx={{ justifyContent: 'space-between' }}
                                                >
                                                    {option.name}
                                                    {selected ? <Check color="primary" /> : null}
                                                </MenuItem>
                                            )}
                                            onChange={(event, newValue, reason) => handleTagChange(item.name, event, newValue, reason)}
                                            value={mapFilterToAutocompleteValue(newFilters.find((filter) => filter.name == item.name))}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label={
                                                        <FormattedMessage id={item.label} values={{ indicator: <RequiredIndicator /> }} />
                                                    }
                                                />
                                            )}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}

                        <Typography sx={{ marginLeft: 1, mt: 2.5, color: '#878E95' }}>
                            <FormattedMessage id="creation-date" />
                        </Typography>

                        {datetimeSection && (
                            <>
                                <Box mt="28px" mb="23px">
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        {datetimeSection?.options.map((item, index) => (
                                            <Box sx={{ mt: 2 }} key={`date-field-${index}`}>
                                                <DatePicker
                                                    inputFormat="dd-MM-yyyy"
                                                    label={<FormattedMessage id={item.label} />}
                                                    value={newFilters.find((f) => f.name === item.name)?.value || null}
                                                    onChange={(date) => {
                                                        if (date) {
                                                            const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                                            setNewFilters([
                                                                ...removedKeyFilter,
                                                                {
                                                                    name: item.name,
                                                                    value: format(new Date(date), 'yyyy-MM-dd'),
                                                                    checked: true
                                                                }
                                                            ]);
                                                        }
                                                    }}
                                                    renderInput={(params) => <TextField {...params} />}
                                                />
                                            </Box>
                                        ))}
                                    </LocalizationProvider>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            <Stack>
                <Box mt={4} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ButtonStyled onClick={() => setNewFilters([])} variant="outlined">
                        <FormattedMessage id="clear" />
                    </ButtonStyled>
                    <ButtonStyled
                        disabled={disabledSave}
                        variant="contained"
                        onClick={() => {
                            onClose();
                            if (setFilters) {
                                setFilters(newFilters);
                            }
                        }}
                    >
                        <FormattedMessage id="applied" />
                    </ButtonStyled>
                </Box>
            </Stack>
        </Box>
    );
};

interface FilterListProps {
    newFilters: RandomSetting[];
    handleToggle: (index: number) => void;
}

export const FilterList = ({ newFilters, handleToggle }: FilterListProps) => (
    <>
        {newFilters.length > 0 && (
            <>
                <Box sx={{ mb: 2.25 }}>
                    <Typography sx={{ marginLeft: 1, mt: 6.25, color: '#878E95' }}>
                        <FormattedMessage id="selected-filters" />
                    </Typography>
                    <List>
                        {newFilters.map((item, index) => (
                            <ListItem key={index} onClick={() => handleToggle(index)}>
                                <Checkbox checked={item.checked} />
                                {item.name.includes('difficultyLevelFrom') && (
                                    <ListItemText primary={<FormattedMessage id="level-from" values={{ level: item.value }} />} />
                                )}
                                {item.name.includes('difficultyLevelTo') && (
                                    <ListItemText primary={<FormattedMessage id="level-to" values={{ level: item.value }} />} />
                                )}
                                {item.name === 'amountQuestions' && (
                                    <ListItemText primary={<FormattedMessage id="amount" values={{ amount: item.value }} />} />
                                )}
                                {item.name === 'questionType' && (
                                    <ListItemText>
                                        {item.display_name?.split(',').map((display_name, key) => (
                                            <Fragment key={key}>
                                                {key ? ',' : ''}
                                                <FormattedMessage id={display_name} />
                                            </Fragment>
                                        ))}
                                    </ListItemText>
                                )}
                                {item.name === 'collectionType' && (
                                    <ListItemText>
                                        {item.display_name?.split(',').map((display_name, key) => (
                                            <Fragment key={key}>
                                                {key ? ',' : ''}
                                                <FormattedMessage id={display_name} />
                                            </Fragment>
                                        ))}
                                    </ListItemText>
                                )}

                                {item.name === 'mainTag' && <ListItemText primary={item.display_name} />}
                                {item.name === 'subTag' && <ListItemText primary={item.display_name} />}
                                {item.name === 'startDate' && (
                                    <ListItemText primary={<FormattedMessage id="start-date-r" values={{ date: item.value }} />} />
                                )}
                                {item.name === 'endDate' && (
                                    <ListItemText primary={<FormattedMessage id="end-date-r" values={{ date: item.value }} />} />
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <Divider color="primary.main" />
            </>
        )}
    </>
);

export default AddObject;
